import React, { useState } from 'react'
import { Form, Input, Button, Card, Typography, Alert, Radio } from 'antd'
import { UserOutlined, LockOutlined, SafetyCertificateOutlined, CrownOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { authAPI } from '../services/api'
import { showSuccessAlert, showErrorAlert, showAuthAlert, showSecurityAlert } from '../utils/customAlert'
import { loginSuccess } from '../utils/auth'

const { Title } = Typography

export default function LoginPage() {
  const [requiresGoogleAuth, setRequiresGoogleAuth] = useState(false);
  const [requiresUserPassword, setRequiresUserPassword] = useState(false);
  const [userType, setUserType] = useState('normal'); // 'admin' 或 'normal'
  const [form] = Form.useForm();

  const onFinish = async (values) => {
    try {
      console.log('🔍 开始登录流程...', values);
      console.log('👤 用户类型:', userType);
      
      // 根据用户类型设置相应的验证字段
      const loginData = {
        ...values,
        userType: userType // 传递用户类型到后端
      };
      
      const response = await authAPI.login(loginData)
      console.log('✅ 登录API调用成功:', response.data);
      
      showSuccessAlert('登录成功！', '登录成功')
      console.log('🚀 准备跳转到 /memos');
      
      // 使用认证工具函数处理登录成功
      loginSuccess(response.data.token, response.data.isAdmin, response.data.userId, response.data.isSuperAdmin);
      
    } catch (error) {
      console.error('❌ 登录失败:', error);
      console.error('错误响应:', error.response?.data);
      console.error('错误状态:', error.response?.status);
      
      // 检查用户类型不匹配错误
      if (error.response?.data?.code === 'USER_TYPE_MISMATCH') {
        showAuthAlert('用户类型不匹配，请选择正确的用户类型', '类型错误');
      } else if (error.response?.status === 403 && error.response?.data?.code === 'USER_PASSWORD_REQUIRED') {
        setRequiresUserPassword(true);
        showSecurityAlert('请输入用户口令', '用户口令必需');
      } else if (error.response?.data?.code === 'USER_PASSWORD_INVALID') {
        showAuthAlert('用户口令错误，请重新输入', '口令错误');
      } else if (error.response?.data?.code === 'GOOGLE_AUTH_REQUIRED') {
        showErrorAlert('管理员账户必须启用谷歌验证码才能登录，请联系超级管理员设置', '谷歌验证码未启用');
      } else if (error.response?.status === 403 && error.response?.data?.code === '2FA_REQUIRED') {
        setRequiresGoogleAuth(true);
        showSecurityAlert('请输入Google Authenticator验证码', 'Google验证码必需');
      } else if (error.response?.data?.code === '2FA_INVALID') {
        showAuthAlert('Google Authenticator验证码无效，请重新输入', '验证码错误');
      } else if (error.response?.status === 401) {
        showAuthAlert('用户名或密码错误，或账户未审核通过', '登录失败');
      } else {
        showErrorAlert('登录失败，请重试', '系统错误');
      }
    }
  }

  // 用户类型改变时的处理
  const handleUserTypeChange = (e) => {
    const newUserType = e.target.value;
    setUserType(newUserType);
    
    // 重置验证状态
    setRequiresGoogleAuth(false);
    setRequiresUserPassword(false);
    
    // 根据用户类型预设置验证字段
    if (newUserType === 'admin') {
      // 管理员用户，如果后端需要谷歌验证码，会自动显示
      console.log('🔐 管理员登录模式');
    } else {
      // 普通用户，如果后端需要用户口令，会自动显示
      console.log('👤 普通用户登录模式');
    }
  }

  return (
    <div style={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center', background: '#f0f2f5' }}>
      <Card style={{ width: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ margin: 0 }}>Memo App</Title>
          <p style={{ color: '#666', margin: '8px 0 0 0' }}>请登录您的账户</p>
        </div>
        
        <Form
          form={form}
          name="login"
          onFinish={onFinish}
          autoComplete="off"
          size="large"
        >
          {/* 用户类型选择 */}
          <Form.Item label="用户类型" style={{ marginBottom: 16 }}>
            <Radio.Group 
              value={userType} 
              onChange={handleUserTypeChange}
              style={{ width: '100%' }}
            >
              <Radio.Button 
                value="normal" 
                style={{ width: '50%', textAlign: 'center' }}
              >
                <UserOutlined /> 普通用户
              </Radio.Button>
              <Radio.Button 
                value="admin" 
                style={{ width: '50%', textAlign: 'center' }}
              >
                <CrownOutlined /> 管理员
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="username"
            rules={[{ required: true, message: '请输入用户名!' }]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="用户名"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码!' }]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="密码" 
            />
          </Form.Item>

          {requiresUserPassword && (
            <>
              <Form.Item
                name="userPassword"
                rules={[
                  { required: true, message: '请输入用户口令!' },
                  { min: 6, message: '用户口令至少6位!' }
                ]}
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="用户口令" 
                />
              </Form.Item>
              
              <Alert
                message="需要用户口令"
                description={userType === 'admin' 
                  ? "管理员用户登录时需要输入用户口令。" 
                  : "普通用户需要输入用户口令才能登录。"}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </>
          )}

          {requiresGoogleAuth && (
            <>
              <Form.Item
                name="googleToken"
                rules={[
                  { required: true, message: '请输入谷歌验证码!' },
                  { pattern: /^\d{6}$/, message: '验证码必须是6位数字!' }
                ]}
              >
                <Input 
                  prefix={<SafetyCertificateOutlined />} 
                  placeholder="谷歌验证码 (6位数字)" 
                  maxLength={6}
                />
              </Form.Item>
              
              <Alert
                message="需要谷歌验证码"
                description={userType === 'admin' 
                  ? "管理员账户已启用谷歌验证码，请输入6位数字验证码。" 
                  : "您的账户已启用谷歌验证码，请输入6位数字验证码。"}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ width: '100%' }}>
              登录
            </Button>
          </Form.Item>
          
          <div style={{ textAlign: 'center' }}>
            <span style={{ color: '#666' }}>还没有账户？</span>
            <Link to="/register" style={{ marginLeft: 8 }}>立即注册</Link>
          </div>
        </Form>
      </Card>
    </div>
  )
}